import React, { FC, isValidElement } from 'react';
import { useResponsiveProp } from '#hooks/use-responsive-prop.ts';
import { VisuallyHidden } from '#components/visually-hidden/visually-hidden.tsx';
import type { GDSLegendProps } from '../types';
import { styled } from '../../../theme';
import { labelLegendStyles } from '../utilities';
import { LabelText } from './label-text';
import { useThemeStrings } from '../../../hooks/use-theme';
import { GenesisCoreInspector } from '../../../test-utils/genesis-core-inspector';

const DISPLAY_NAME = 'FieldsetLegend';

const LegendBase = styled('legend', labelLegendStyles);

export const FieldsetLegend: FC<GDSLegendProps> = ({
  hideLegend,
  children,
  required,
  tooltip,
  className,
  css,
  'data-testid': testId = `GDS-${DISPLAY_NAME}`,
  ...props
}: GDSLegendProps) => {
  const responsiveHideLegend = useResponsiveProp(hideLegend);
  const { optional } = useThemeStrings();

  if (responsiveHideLegend)
    return (
      <VisuallyHidden as="legend" {...props}>
        {children}
        {!required && `(${optional})`}
      </VisuallyHidden>
    );

  return (
    <GenesisCoreInspector displayName={DISPLAY_NAME}>
      <LegendBase
        className={['GDS-fieldset-legend', className].join(' ')}
        css={css}
        data-testid={testId}
        {...props}
      >
        <LabelText required={required}>{children}</LabelText>
        {isValidElement(tooltip) ? tooltip : null}
      </LegendBase>
    </GenesisCoreInspector>
  );
};

FieldsetLegend.displayName = DISPLAY_NAME;
